import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import Sidebar from '../layouts/Sidebar';
import Badge from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';
import Tabs from '../components/ui/Tabs';
import ReportService from '../services/ReportService';

const STATUS_BADGE = { Pending: 'amber', Active: 'green', Closed: 'neutral', Rejected: 'red' };

const REPORT_TABS = [
  { value: 'incidents', label: 'Incidents & Drills' },
  { value: 'members', label: 'Team Readiness' },
  { value: 'inventory', label: 'Inventory' },
];

function Kpi({ label, value, hint }) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-6 dark:bg-ink-900 dark:border-neutral-700">
      <p className="kpi-label">{label}</p>
      <h3 className="kpi-value">{value}</h3>
      {hint && <p className="text-xs text-neutral-400 mt-1 dark:text-neutral-500">{hint}</p>}
    </div>
  );
}

Kpi.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  hint: PropTypes.node,
};

export default function Reports() {
  const [tab, setTab] = useState('incidents');
  const [overview, setOverview] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [members, setMembers] = useState([]);
  const [inventory, setInventory] = useState({ summary: [], items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ov, inc, mem, inv] = await Promise.all([
          ReportService.overview(),
          ReportService.incidents(),
          ReportService.members(),
          ReportService.inventory(),
        ]);
        setOverview(ov.data);
        setIncidents(inc.data);
        setMembers(mem.data);
        setInventory(inv.data);
      } catch {
        toast.error('Failed to load reports.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const incidentCells = [
    {
      id: 'title',
      label: 'Event',
      render: (row) => (
        <div>
          <div className="font-bold text-neutral-800 dark:text-neutral-100">{row.title}</div>
          <div className="text-xs text-neutral-500 mt-0.5 dark:text-neutral-500">{row.location || '—'}</div>
        </div>
      ),
    },
    {
      id: 'types',
      label: 'Type',
      render: (row) => (
        <div className="flex flex-wrap gap-1.5">
          {(row.types || []).map((t) => <Badge key={t}>{t}</Badge>)}
          <Badge variant={row.drill ? 'blue' : 'neutral'}>{row.drill ? 'Drill' : 'Incident'}</Badge>
        </div>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <Badge variant={STATUS_BADGE[row.status] || 'neutral'}>{row.status}</Badge>,
    },
    {
      id: 'assigned',
      label: 'Response',
      render: (row) => (
        <span className="text-xs text-neutral-600 dark:text-neutral-300">
          <b className="text-green-700 dark:text-green-400">{row.accepted}</b> accepted ·{' '}
          <b className="text-red-600 dark:text-red-400">{row.rejected}</b> rejected ·{' '}
          <b className="text-neutral-500 dark:text-neutral-500">{row.pending}</b> pending
        </span>
      ),
    },
    {
      id: 'avgResponseMinutes',
      label: 'Avg Response',
      render: (row) => (
        <span className="text-neutral-600 dark:text-neutral-300">
          {row.avgResponseMinutes != null ? `${row.avgResponseMinutes} min` : '—'}
        </span>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      render: (row) => (
        <span className="text-xs text-neutral-500 dark:text-neutral-500">{new Date(row.createdAt).toLocaleDateString()}</span>
      ),
    },
  ];

  const memberCells = [
    {
      id: 'fullname',
      label: 'Member',
      render: (row) => (
        <div>
          <div className="font-bold text-neutral-800 dark:text-neutral-100">{row.fullname}</div>
          <div className="text-xs text-neutral-500 mt-0.5 dark:text-neutral-500">{row.email}</div>
        </div>
      ),
    },
    {
      id: 'role',
      label: 'Role',
      render: (row) => <Badge variant="blue">{row.role || '—'}</Badge>,
    },
    {
      id: 'memberStatus',
      label: 'Availability',
      render: (row) => (
        <Badge variant={row.memberStatus === 'Available' ? 'green' : 'neutral'}>{row.memberStatus || '—'}</Badge>
      ),
    },
    { id: 'assigned', label: 'Assigned' },
    { id: 'accepted', label: 'Accepted' },
    { id: 'rejected', label: 'Rejected' },
    {
      id: 'acceptanceRate',
      label: 'Acceptance',
      render: (row) => (
        <span className="text-neutral-700 font-medium dark:text-neutral-300">
          {row.acceptanceRate != null ? `${row.acceptanceRate}%` : '—'}
        </span>
      ),
    },
  ];

  const inventoryCells = [
    { id: 'category', label: 'Category', render: (row) => <Badge>{row.category}</Badge> },
    { id: 'items', label: 'Items' },
    { id: 'totalQuantity', label: 'Total Stock' },
    {
      id: 'lowStock',
      label: 'Low Stock',
      render: (row) => (
        row.lowStock > 0 ? <Badge variant="red">{row.lowStock}</Badge> : <Badge variant="green">0</Badge>
      ),
    },
    {
      id: 'expiringSoon',
      label: 'Expiring ≤ 90d',
      render: (row) => (
        row.expiringSoon > 0 ? <Badge variant="amber">{row.expiringSoon}</Badge> : <Badge variant="green">0</Badge>
      ),
    },
  ];

  return (
    <Sidebar title="Reports & KPI">
      <section className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Kpi
            label="Incidents"
            value={overview ? overview.incidents.total : '…'}
            hint={overview ? `${overview.incidents.active} active · ${overview.incidents.closed} closed` : ''}
          />
          <Kpi
            label="Drills"
            value={overview ? overview.drills.total : '…'}
            hint={overview ? `${overview.drills.active} active · ${overview.drills.closed} closed` : ''}
          />
          <Kpi
            label="Assignment Acceptance"
            value={
              overview && overview.assignments.total > 0
                ? `${Math.round((overview.assignments.accepted / overview.assignments.total) * 100)}%`
                : '—'
            }
            hint={overview ? `${overview.assignments.accepted} of ${overview.assignments.total} assignments` : ''}
          />
          <Kpi
            label="Avg Response Time"
            value={
              overview && overview.assignments.avgResponseMinutes != null
                ? `${overview.assignments.avgResponseMinutes} min`
                : '—'
            }
            hint="Assignment to member response"
          />
        </div>

        <Tabs tabs={REPORT_TABS} active={tab} onChange={setTab} />

        {tab === 'incidents' && (
          <DataTable
            title="Incident & Drill Report"
            headCells={incidentCells}
            rows={incidents}
            loading={loading}
            emptyLabel={loading ? 'Loading…' : 'No events recorded.'}
          />
        )}

        {tab === 'members' && (
          <DataTable
            title="Team Readiness & Participation"
            headCells={memberCells}
            rows={members}
            loading={loading}
            emptyLabel={loading ? 'Loading…' : 'No members found.'}
          />
        )}

        {tab === 'inventory' && (
          <DataTable
            title="Inventory Summary by Category"
            headCells={inventoryCells}
            rows={inventory.summary.map((row, idx) => ({ id: idx, ...row }))}
            loading={loading}
            emptyLabel={loading ? 'Loading…' : 'No inventory data.'}
          />
        )}
      </section>
    </Sidebar>
  );
}
